import React, { createContext, useEffect, useState, ReactNode } from "react";
import { getAppConfig } from "@config/appConfig";
import { SITE_DARK_THEME, SITE_LIGHT_THEME } from "@config/config";
import { useBannerCarousel } from "@queries/useBannerCarousel";
import { useCinemas } from "@queries/useCinemas";
import { useFeatureFilms } from "@queries/useFeatureFilms";
import { useMembershipOptions } from "@queries/useMembershipOptions";
import { useSeasonalRoutes } from "@queries/useSeasonalRoutes";
import { useSubscriberGroup } from "@queries/useSubscriberGroup";
import { useWhiteLabel } from "@queries/useWhiteLabel";

interface Cinema {
    type: string;
    id: number;
    name: string;
    link_url: string;
    large_image_url: string;
    thumbnail_url: string;
    accessibility: number;
    audio_description: number;
    close_caption: number;
    open_caption: number;
    info_address: string;
    info_postcode: string;
    info_state: string;
    info_screens: number;
    latitude: string;
    longitude: string;
    venue_description: string;
    hide_sessions_show_nearby: number;
}

interface CarouselImage {
    bo_movie_id?: number;
    bo_movie_title?: string;
    banner_image_url?: string;
    banner_trailer_url?: string;
    description?: string;
    feature_url: string;
    trailer_url: string;
    trailer_available: boolean;
    feature_film_flag: boolean;
    ad_category_url: string;
    ad_category_id?: number;
    ad_category_type?: number;
    image_source?: number;
}

interface FeatureFilm {
    ad_category_url: string;
    bo_movie_id: number;
    bo_movie_title: string;
    feature_film_flag: boolean;
    feature_url?: string;
    trailer_available: boolean;
    trailer_url: string;
}

interface MembershipOption {
    id: number;
    name: string;
    description: string;
    active: boolean;
    created: string;
    modified: string;
    membership_period: number;
    membership_price: string;
    membership_type: number;
    promo_code: number;
    subscriber_group_id: number;
}

interface News {
    id: number | null;
    title: string;
    text: string;
    image: string | null;
    link: string | null;
    content: string | null;
}

interface NewsItem {
    id?: number;
    title?: string;
    text?: string;
    image?: string | null;
    link?: string | null;
    content?: string | null;
}

interface TicketClassSubscriberGroup {
    id: number;
    subscriber_group_id: number;
    ticket_class_id: number;
    active: boolean;
}

interface SubscriberGroup {
    id: number;
    product_type_id: number;
    authentication_type: number;
    add_display_name: boolean;
    bypass_lets_get_started: boolean;
    prompt_user_for_email: boolean;
    cashback_flag: boolean;
    display_price: number;
    enable_guest_experience: boolean;
    jwt_authentication_type: number;
    logo_path: string | null;
    partner_integrate_authentication_type: number;
    subscriber_email_maintenance: boolean;
    subscriber_group_get_started: string | null;
    subscriber_group_welcome_page: string | null;
    ticket_class_subscriber_group: TicketClassSubscriberGroup[];
    vm_wholesale: boolean;
}

interface WhiteLabel {
    id: number;
    subscriber_group_id: number;
    brand_code: string;
    brand_name: string;
    brand_title: string;
    admin_email: string;
    support_email: string;
    button_text_colour: string;
    by_line: string;
    candy_bar_items: boolean;
    created: string;
    modified: string;
    data_collection_use_api: boolean;
    voucher_terms_api: boolean;
    enable_agentic_ai: boolean;
    agentic_ai_cinema_groups: number[];
    agentic_ai_ticket_classes: number[];
    enable_gifting: boolean;
    faq_use_api: boolean;
    favicon_image_url: string;
    font_family: string;
    font_url: string;
    free_tickets_text: string;
    ga_code: string;
    ga_code_domain: string;
    home_page_title: string;
    logo_image_email_url: string;
    logo_image_url: string;
    meta_description: string;
    no_public_access_body: string;
    no_public_access_link_url: string;
    no_public_access_link_value: string;
    no_public_access_title: string;
    page_title: string;
    participating_cinema_description: string;
    payment_gateway: "eway" | "stripe";
    stripe_public_key: string | null;
    preferred_name: string;
    primary_colour: string;
    privacy_use_api: boolean;
    public_subscriber_group: number;
    redemptions_bar_text: string;
    referral_code_flag: boolean;
    referral_placeholder_text: string;
    require_login: boolean;
    secondary_colour: string;
    show_member_no: boolean;
    show_participating_cinemas: boolean;
    show_ticket_class: boolean;
    site_theme: number;
    site_version: number;
    terms_use_api: boolean;
    tertiary_colour: string;
    active: boolean;
    subscriber_group: SubscriberGroup;
    ticket_class_subscriber_group: TicketClassSubscriberGroup[];
}

interface SeasonalRoute {
    name: string;
    advert_url: string;
}

interface ApplicationContextType {
    cinemas: Cinema[];
    fetchSubscriberGroup: (groupCode: string) => void;
    isLoading: boolean;
    setCinemas: () => void;
    subscriberGroup: SubscriberGroup;
    whiteLabel: WhiteLabel | null;
    fetchWhiteLabel: () => void;
    fetchSeasonalRoutes: () => void;
    seasonalRoutes: SeasonalRoute[] | null;
    news: News;
    setNews: React.Dispatch<React.SetStateAction<News>>;
    newsItem: NewsItem;
    setNewsItem: React.Dispatch<React.SetStateAction<NewsItem>>;
    appSecurityLogIn: boolean;
    featureFilm: FeatureFilm[] | null;
    setFeatureFilm: () => void;
    isLoadingFeatureFilms: boolean;
    carouselImages: CarouselImage[] | null;
    isLoadingCarousel: boolean;
    cinemasLoading: boolean;
    productTypeId: number;
    publicSubscriberGroup: number;
    subscriberGroupId: number;
    membershipOptions: MembershipOption[] | null;
    isMembershipLoading: boolean;
}

export const ApplicationContext = createContext<ApplicationContextType>({
    cinemas: [],
    fetchSubscriberGroup: (_groupCode: string) => {},
    isLoading: false,
    setCinemas: () => {},
    subscriberGroup: {} as SubscriberGroup,
    whiteLabel: null,
    fetchWhiteLabel: () => {},
    fetchSeasonalRoutes: () => {},
    seasonalRoutes: null,
    news: {
        id: null,
        title: "",
        text: "",
        image: null,
        link: null,
        content: null,
    },
    setNews: () => {},
    newsItem: {},
    setNewsItem: () => {},
    appSecurityLogIn: false,
    featureFilm: null,
    setFeatureFilm: () => {},
    isLoadingFeatureFilms: false,
    carouselImages: null,
    isLoadingCarousel: false,
    cinemasLoading: false,
    productTypeId: 0,
    publicSubscriberGroup: 0,
    subscriberGroupId: 0,
    membershipOptions: null,
    isMembershipLoading: false,
});

interface ApplicationContextProviderProps {
    children: ReactNode;
}

const ApplicationContextProvider: React.FC<ApplicationContextProviderProps> = ({ children }) => {

    const config = getAppConfig();

    const [news, setNews] = useState<News>({
        id: null,
        title: "",
        text: "",
        image: null,
        link: null,
        content: null,
    });
    const [newsItem, setNewsItem] = useState<NewsItem>({});

    const { cinemas, cinemasLoading } = useCinemas();
    const [groupCode, setGroupCode] = useState<string | null>(null);
    const { subscriberGroup, isLoading: isLoadingGroup } = useSubscriberGroup(groupCode);
    const resolvedSubscriberGroupId = subscriberGroup?.id || config?.subscriberGroupId || 0;
    const { whiteLabel, isLoading: isLoadingWhiteLabel, appSecurityLogIn } = useWhiteLabel(resolvedSubscriberGroupId);
    const { carouselImages, isLoading: isLoadingCarousel } = useBannerCarousel(resolvedSubscriberGroupId);
    const productTypeId = subscriberGroup?.product_type_id || config?.productTypeId || 0;
    const publicSubscriberGroup = whiteLabel?.public_subscriber_group ?? config?.publicSubscriberGroup ?? 0;
    const isPublicSite = publicSubscriberGroup === 1;

    const { featureFilm, isLoadingFeatureFilms } = useFeatureFilms(resolvedSubscriberGroupId);
    const { membershipOptions, isMembershipLoading } = useMembershipOptions(resolvedSubscriberGroupId, isPublicSite);
    const { seasonalRoutes } = useSeasonalRoutes();


    const fetchSubscriberGroup = (code: string) => {
        setGroupCode((prev) => prev === code ? prev : code);
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const codeFromQuery = params.get("code");
        if (codeFromQuery && !groupCode) {
            fetchSubscriberGroup(codeFromQuery);
        }
    }, []);

    useEffect(() => {
        const { body } = document;
        body.classList.remove("light-theme", "dark-theme", "current-gen", "next-gen");

        if (!whiteLabel) return;

        const siteTheme = whiteLabel?.site_theme;

        if (siteTheme === SITE_LIGHT_THEME) {
            body.classList.add("light-theme");
        } else if (siteTheme === SITE_DARK_THEME) {
            body.classList.add("dark-theme");
        }
    }, [whiteLabel?.site_theme]);


    const isLoading = isLoadingGroup || isLoadingWhiteLabel || (!!groupCode && !subscriberGroup?.id);

    return (
        <ApplicationContext.Provider
            value={{
                cinemas,
                cinemasLoading,
                setCinemas: () => {},
                fetchSubscriberGroup,
                isLoading,
                subscriberGroup,
                whiteLabel,
                fetchWhiteLabel: () => {},
                fetchSeasonalRoutes: () => {},
                seasonalRoutes,
                news,
                setNews,
                newsItem,
                setNewsItem,
                appSecurityLogIn,
                featureFilm,
                setFeatureFilm: () => {},
                isLoadingFeatureFilms,
                productTypeId,
                publicSubscriberGroup,
                subscriberGroupId: resolvedSubscriberGroupId,
                carouselImages,
                isLoadingCarousel,
                membershipOptions,
                isMembershipLoading,
            }}
        >
            {children}
        </ApplicationContext.Provider>
    );
};

export default ApplicationContextProvider;
